import type { ReactNode } from 'react';

import { useEffect, useMemo, useState } from 'react';

import type { ColorTheme } from '~/api/user/types';

import { colorThemes } from '~/api/user/constants';
import { SearchParamKeys } from '~/constants/url';
import { useCraftLocalStorage } from '~/hooks/useCraftLocalStorage';
import { brandColorThemes, lightColorTheme } from '~/styles/themes.css';
import { includes } from '~/utils/arrays';

import { ColorThemeContext } from './ColorThemeProvider.context';

type Props = {
  children: ReactNode;
};

export const ColorThemeProvider = ({ children }: Props) => {
  const [storedColorTheme, setStoredColorTheme] = useCraftLocalStorage('colorTheme');

  const searchParams = new URLSearchParams(window.location.search);
  const colorThemeSearchParam = searchParams.get(SearchParamKeys.COLOR_THEME);
  const urlColorTheme = includes(colorThemes, colorThemeSearchParam) ? colorThemeSearchParam : null;

  // A stored theme can be unknown (e.g. from an older build), which would leave the app without brand colors.
  const validStoredColorTheme = includes(colorThemes, storedColorTheme) ? storedColorTheme : null;

  const activeColorTheme = validStoredColorTheme ?? urlColorTheme ?? 'green-pink';

  const context = useMemo(
    () => ({ colorTheme: activeColorTheme, setColorTheme: setStoredColorTheme }),
    [activeColorTheme, setStoredColorTheme],
  );

  // Apply the active color theme to the document body
  const isInitialColorThemeApplied = useApplyColorTheme(activeColorTheme);

  if (!isInitialColorThemeApplied) return null;

  return <ColorThemeContext value={context}>{children}</ColorThemeContext>;
};

const useApplyColorTheme = (colorTheme: ColorTheme): boolean => {
  // Prevent rendering UI until the initial light color theme and brand color theme has been applied.
  // Otherwise, elements will have the wrong color because the CSS variables are not available yet.
  const [isInitialColorThemeApplied, setIsInitialColorThemeApplied] = useState(false);

  useEffect(() => {
    if (!isInitialColorThemeApplied) {
      document.body.classList.add(...lightColorTheme.split(' '));
      setIsInitialColorThemeApplied(true);
    }

    Object.values(brandColorThemes).forEach((className) => document.body.classList.remove(className));
    document.body.classList.add(brandColorThemes[colorTheme]);
  }, [colorTheme, isInitialColorThemeApplied]);

  return isInitialColorThemeApplied;
};
